/**
 * Fire Weather Index (FWI) heatmap layer (Phase E.2.2) — the second operator-
 * visible Fire Risk layer, and the platform's first *imagery* overlay (every
 * other layer is vector entities or a GeoJsonDataSource).
 *
 * Renders the CWFIS Fire Weather Index grid exactly as the government displays
 * it, by consuming the CWFIS WMS `public:fwi_current` layer **directly at
 * view-time** — no ingest, no DB table, no migration. The decision rests on two
 * live-verified facts (docs/phase-e2-scope/e2.2-fwi-discovery.md):
 *   1. CWFIS serves WMS tiles with `Access-Control-Allow-Origin: *`, so Cesium
 *      can fetch them cross-origin straight from the browser (no proxy).
 *   2. `fwi_current` is an always-latest layer with NO time dimension — the
 *      layer name is itself the "latest available" selector, so no TIME param.
 *
 * The empty `styles` parameter selects the server default style `cffdrs_fwi`
 * (the official CWFIS colour ramp the operator recognises from their own tools —
 * see FwiLegend). EPSG:4326 is requested explicitly for Cesium compatibility
 * (Q.1.1 lesson) even though the native grid is EPSG:3978.
 *
 * Like ProvincialBoundariesLayer / ActiveFiresLayer, MapView owns the Cesium
 * viewer, so this is an imperative installer that returns a handle. The layer is
 * added to `viewer.imageryLayers` (not `entities`) and is shown/hidden via the
 * Weather-group radio toggle.
 */
import * as Cesium from 'cesium';

/** CWFIS WMS endpoint (CWFIS main GeoServer — hosts the raster grids). */
export const FWI_WMS_URL = 'https://cwfis.cfs.nrcan.gc.ca/geoserver/public/wms';
/** The FWI grid layer (verified live in E.2.2.1). */
export const FWI_LAYER = 'public:fwi_current';
/** Default overlay opacity — visible but markers/fires below stay readable. */
export const FWI_DEFAULT_ALPHA = 0.55;
/** Data extent (CRS:84) from GetCapabilities — scope tiling to Canada so we
 *  don't fan out global tile requests for a grid that is blank outside it. */
const FWI_RECTANGLE = Cesium.Rectangle.fromDegrees(-174.3, 35.8, -11.4, 83.4);

export interface FwiHeatmapLayerHandle {
  /** Show/hide the FWI imagery overlay (Weather-group radio toggle, L.1). */
  setVisible(visible: boolean): void;
  /** Currently-visible state (mirrors the underlying ImageryLayer.show). */
  isVisible(): boolean;
  destroy(): void;
}

/**
 * Build the CWFIS FWI `WebMapServiceImageryProvider`. Pure (no viewer) so it is
 * unit-testable; MapView wraps it via `installFwiHeatmapLayer`.
 *
 * WMS 1.3.0 + `crs=EPSG:4326` means lat,lon axis order — Cesium's provider
 * handles the axis swap internally given the GeographicTilingScheme (default),
 * so we never hand-build a bbox.
 */
export function buildFwiImageryProvider(
  url: string = FWI_WMS_URL,
): Cesium.WebMapServiceImageryProvider {
  return new Cesium.WebMapServiceImageryProvider({
    url,
    layers: FWI_LAYER,
    parameters: {
      // Empty styles → server default `cffdrs_fwi` (the official colour ramp).
      styles: '',
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
    },
    // For WMS >= 1.3.0 the axis-ordered CRS is passed via `crs` (not `srs`).
    crs: 'EPSG:4326',
    rectangle: FWI_RECTANGLE,
    // No GetFeatureInfo — the FWI grid is a background gradient, not clickable
    // (clicks fall through to the markers/fires above it).
    enablePickFeatures: false,
  });
}

/**
 * Install the FWI heatmap as a Cesium imagery overlay. Added on top of the base
 * imagery (and below entity markers, which are screen-space). Starts hidden;
 * MapView calls `setVisible` from the Weather-group toggle effect.
 *
 * Defensive (E.2.2.7 #2): a WMS 4xx/5xx surfaces on the provider's `errorEvent`;
 * we log it and let Cesium retry/skip the tile — a failed FWI fetch must never
 * break the rest of the map.
 */
export function installFwiHeatmapLayer(
  viewer: Cesium.Viewer,
  opts: { alpha?: number; provider?: Cesium.WebMapServiceImageryProvider } = {},
): FwiHeatmapLayerHandle {
  const provider = opts.provider ?? buildFwiImageryProvider();
  // Log tile failures without throwing (don't break the map). Cesium handles
  // its own backoff/retry; this is observability only.
  const removeErr = provider.errorEvent.addEventListener((err: unknown) => {
    console.warn('[FwiHeatmapLayer] CWFIS WMS tile error (non-fatal)', err);
  });

  const layer = viewer.imageryLayers.addImageryProvider(provider);
  layer.alpha = opts.alpha ?? FWI_DEFAULT_ALPHA;
  layer.show = false; // radio toggle turns it on

  return {
    setVisible(visible: boolean): void {
      if (viewer.isDestroyed()) return;
      layer.show = visible;
    },
    isVisible(): boolean {
      return !viewer.isDestroyed() && layer.show;
    },
    destroy(): void {
      removeErr();
      if (viewer.isDestroyed()) return;
      // `true` → destroy the underlying provider/resources too.
      viewer.imageryLayers.remove(layer, true);
    },
  };
}
